"use client";

import { useState } from "react";
import Link from "next/link";
import { WORKER_BLUEPRINTS } from "@/lib/blueprints/catalog";

export default function OnboardingWizard({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [step, setStep] = useState<number>(1);
  const [selectedBlueprint, setSelectedBlueprint] = useState<string>(
    WORKER_BLUEPRINTS[0]?.id ?? "customer-support-triage"
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-label="Quick start guide" className="max-h-[calc(100dvh-2rem)] w-full max-w-xl overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800/80 pb-3">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-violet-600 text-xs font-bold text-white">
              {step}
            </span>
            <h2 className="text-base font-semibold text-zinc-100">
              {step === 1 && "Welcome to Create a Worker"}
              {step === 2 && "Choose Your First Blueprint"}
              {step === 3 && "Set Up Your Worker"}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close quick start guide"
            className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          >
            ✕
          </button>
        </div>

        <div className="py-4">
          {step === 1 && (
            <div className="space-y-3 text-xs text-zinc-300">
              <p className="text-sm text-zinc-200">
                Create a Worker powers autonomous business operations with deterministic workflows, human-in-the-loop approvals, and multi-channel monitoring.
              </p>
              <div className="grid gap-2 pt-2">
                <div className="rounded-xl border border-zinc-800/80 bg-zinc-900/50 p-3">
                  <span className="font-semibold text-zinc-100">1. Pre-built Blueprints</span>
                  <p className="text-zinc-400 mt-0.5">
                    1-click workers for support triage, daily briefs, invoice extraction, and CRM updates.
                  </p>
                </div>
                <div className="rounded-xl border border-zinc-800/80 bg-zinc-900/50 p-3">
                  <span className="font-semibold text-zinc-100">2. Safe & Deterministic</span>
                  <p className="text-zinc-400 mt-0.5">
                    All writes run in deterministic code; LLMs only extract and decide.
                  </p>
                </div>
                <div className="rounded-xl border border-zinc-800/80 bg-zinc-900/50 p-3">
                  <span className="font-semibold text-zinc-100">3. Human in the Loop</span>
                  <p className="text-zinc-400 mt-0.5">
                    Urgent actions pause for approval in Monitoring or Slack.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-400">
                Pick a template to instantiate your first autonomous automation:
              </p>
              <div className="grid gap-2 max-h-64 overflow-y-auto">
                {WORKER_BLUEPRINTS.map((bp) => (
                  <button
                    key={bp.id}
                    type="button"
                    onClick={() => setSelectedBlueprint(bp.id)}
                    className={`rounded-xl border p-3 text-left transition ${
                      selectedBlueprint === bp.id
                        ? "border-violet-500/60 bg-violet-600/15"
                        : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-zinc-100">{bp.title}</span>
                      <span className="text-[10px] text-zinc-500 uppercase">{bp.department}</span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-[11px] text-zinc-400">{bp.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 text-center py-4">
              <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-emerald-500/20 text-2xl text-emerald-300">
                ✓
              </div>
              <div>
                <h3 className="text-sm font-semibold text-zinc-100">Your template is ready</h3>
                <p className="mt-1 text-xs text-zinc-400">
                  Create your worker, connect the tools it needs, and pass a test before activating it.
                </p>
              </div>
              <div className="pt-2 flex flex-wrap justify-center gap-3">
                <Link
                  href={`/workers/new?blueprint=${selectedBlueprint}`}
                  onClick={onClose}
                  className="rounded-lg bg-violet-600 px-4 py-2 text-xs font-medium text-white hover:bg-violet-500"
                >
                  Create Selected Worker
                </Link>
                <Link
                  href="/knowledge"
                  onClick={onClose}
                  className="rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-800"
                >
                  Explore Knowledge Base
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-zinc-800/80 pt-3">
          {step > 1 && step < 3 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="text-xs text-zinc-400 hover:text-zinc-200"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              className="rounded-lg bg-violet-600 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-violet-500"
            >
              Next Step
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-zinc-500 hover:text-zinc-300"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
